// src/components/PageHeader.js

import React, { useState, useEffect } from 'react';
import { Plus, Search, X, HelpCircle, Menu, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { AnimatePresence, motion } from "framer-motion";
import { useMediaQuery } from "../hooks/use-media-query";
import { MobileMenuButton } from "./SideNav";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogClose
} from "../components/ui/dialog";

const PageHeader = ({
  title,
  description,
  icon: Icon,
  tooltip,
  searchQuery = '',
  onSearchChange,
  onAddClick,
  filterButton = null,
  className = '',
  children,
  tabs = [],
  activeTab = '',
  onTabChange,
  contentSection = null,
  isOnboarding = false,
  helpContent = null,
  helpTitle = 'Help & Documentation',
  addButtonClassName = ""
}) => {
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width: 640px)");

  // Function to handle opening the help panel
  const handleOpenHelp = () => {
    // First, close the dialog
    setIsHelpOpen(false);

    // Then, call the helpContent function if it exists
    if (typeof helpContent === 'function') {
      setTimeout(() => {
        helpContent();
      }, 100); // Short delay to ensure dialog is closed
    }
  };

  useEffect(() => {
    // Animate header in on mount
    setIsHeaderVisible(true);

    // Set document title to match the header title
    // This ensures consistent titles during lazy loading
    if (title) {
      document.title = title;
    }
  }, [title]);

  // Determine if we have a simple layout case (no search + only one button) for mobile
  const hasSearch = !!onSearchChange;
  const buttonCount = (filterButton ? 1 : 0) + (onAddClick ? 1 : 0) + (React.Children.count(children) > 0 ? 1 : 0);
  const isSimpleMobileLayout = isMobile && !hasSearch && buttonCount === 1;

  // Calculate the height for the spacer element when header is fixed on mobile - more precise calculation
  const calculateHeaderHeight = () => {
    // Base height for title row
    let baseHeight = 70; // ~h-16 plus padding

    // Add height for description if present
    if (description) {
      baseHeight += isMobile ? 40 : 30;
    }

    // Add height for tabs section if present
    if (tabs.length > 0) {
      baseHeight += 50;
    }

    // Add height for search/buttons row if not in simple layout
    if (!isSimpleMobileLayout && (hasSearch || buttonCount > 0)) {
      baseHeight += 60;
    }

    // Add some buffer for different screen sizes and text lengths
    const buffer = 10;

    return `${baseHeight + buffer}px`;
  };

  // Determine which single button to show (if we're in simple layout)
  const singleButton = onAddClick ? (
    <motion.div
      whileHover={{ 
        scale: 1.02,
        transition: { duration: 0.2, ease: "easeOut" }
      }}
      whileTap={{ 
        scale: 0.98,
        transition: { duration: 0.1, ease: "easeIn" }
      }}
      className="flex-shrink-0"
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        type: "spring", 
        stiffness: 300, 
        damping: 15,
        delay: 0.3
      }}
    >
      <Button 
        variant="outline" 
        size="icon" 
        className={`h-10 w-10 flex items-center justify-center p-0 shadow-sm border-gray-200 ${addButtonClassName}`}
        onClick={onAddClick}
        aria-label="Add new"
      >
        <Plus className="h-4 w-4" />
      </Button>
    </motion.div>
  ) : filterButton ? (
    <motion.div
      className="flex-shrink-0 relative"
      whileHover={{ 
        scale: 1.02,
        transition: { duration: 0.2, ease: "easeOut" }
      }}
      whileTap={{ 
        scale: 0.98,
        transition: { duration: 0.1, ease: "easeIn" }
      }}
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        type: "spring", 
        stiffness: 300, 
        damping: 15,
        delay: 0.25
      }}
    >
      {filterButton}
    </motion.div>
  ) : children;

  // Function to toggle the mobile menu by dispatching a custom event
  const handleToggleMobileMenu = () => {
    const event = new CustomEvent('toggle-mobile-menu');
    window.dispatchEvent(event);
  };

  // Help button component - Only show if helpContent is provided
  const HelpButton = helpContent && (
    <motion.div
      whileHover={{ 
        scale: 1.02,
        transition: { duration: 0.2, ease: "easeOut" }
      }}
      whileTap={{ 
        scale: 0.98,
        transition: { duration: 0.1, ease: "easeIn" }
      }}
      className="flex-shrink-0"
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        type: "spring", 
        stiffness: 300, 
        damping: 15,
        delay: 0.3
      }}
    >
      <Button 
        variant="outline" 
        size="icon" 
        className="h-9 w-9 sm:h-10 sm:w-10 flex items-center justify-center p-0 shadow-sm border-gray-200"
        onClick={handleOpenHelp}
        aria-label="Help & Documentation"
      >
        <HelpCircle className="h-4 w-4 text-gray-700" />
      </Button>
    </motion.div>
  );

  return (
    <>
      {/* Spacer element to prevent content from being hidden under fixed header on mobile */}
      {isMobile && <div style={{ height: calculateHeaderHeight() }} />}

      <motion.div 
        className={`
          ${isMobile 
            ? 'fixed top-0 left-0 right-0 z-[9990] bg-white shadow-md' 
            : tabs.length === 0 
              ? 'border-b border-gray-100' 
              : ''}
          ${className}
        `}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: isHeaderVisible ? 1 : 0, y: isHeaderVisible ? 0 : -10 }}
        transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
      >
        <div className={`
          ${isMobile ? 'py-3 px-4' : 'container mx-auto py-5 sm:py-6 px-4 sm:px-6 max-w-7xl'}
        `}>
          {/* Title and Actions Row */}
          <div className={`flex ${isSimpleMobileLayout ? 'flex-row items-center justify-between' : 'flex-col sm:flex-row sm:items-center justify-between'} mb-3 sm:mb-4 gap-3 sm:gap-6`}>
            {/* Left side - Title and optional tooltip */}
            <motion.div 
              className="flex items-center space-x-3 sm:space-x-4"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2, duration: 0.4 }}
            >
              {isMobile && !isOnboarding ? (
                <motion.div
                  whileHover={{ 
                    scale: 1.02,
                    transition: { duration: 0.2, ease: "easeOut" }
                  }}
                  whileTap={{ 
                    scale: 0.98,
                    transition: { duration: 0.1, ease: "easeIn" }
                  }}
                  className="flex-shrink-0"
                >
                  <Button 
                    variant="outline" 
                    size="icon" 
                    className="h-9 w-9 sm:h-10 sm:w-10 flex items-center justify-center p-0 shadow-sm border-gray-200"
                    onClick={handleToggleMobileMenu}
                    aria-label="Toggle menu"
                  >
                    <Menu className="h-4 w-4" />
                  </Button>
                </motion.div>
              ) : Icon && (
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  className="flex-shrink-0"
                >
                  <Icon className="h-6 w-6 sm:h-7 sm:w-7 text-gray-700" />
                </motion.div>
              )}

              <div className="flex flex-col">
                <h1 className={`${isMobile ? 'text-xl' : 'text-2xl sm:text-3xl'} font-bold tracking-tight truncate`}>{title}</h1>

                {/* Description for mobile view (placed directly under title) */}
                {isMobile && description && (
                  <motion.p 
                    className="text-xs sm:text-sm text-gray-500 mt-1"
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.3 }}
                  >
                    {description}
                  </motion.p>
                )}
              </div>

              {tooltip && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <motion.div 
                        whileHover={{ scale: 1.1 }} 
                        whileTap={{ scale: 0.95 }}
                        className="flex-shrink-0"
                      >
                        <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-9 sm:w-9 rounded-full">
                          <HelpCircle className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500" />
                        </Button>
                      </motion.div>
                    </TooltipTrigger>
                    <TooltipContent 
                      className="bg-white p-3 sm:p-4 text-gray-700 text-sm max-w-xs rounded-md border border-gray-200 shadow-lg"
                      sideOffset={5}
                      align={isMobile ? "center" : "start"}
                    >
                      <p className="leading-relaxed">{tooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </motion.div>

            {/* Show the single button inline for simple mobile layouts */}
            {isSimpleMobileLayout && singleButton}

            {/* Right side - Search, filter, custom elements, and Add button - Only show for desktop or complex mobile layouts */}
            {(!isSimpleMobileLayout) && (
              <motion.div 
                className="flex w-full sm:w-auto items-center space-x-2 sm:space-x-3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.4 }}
              >
                {onSearchChange && (
                  <motion.div 
                    className={`relative flex-1 w-full sm:w-[300px] md:w-[350px] transition-all duration-300 ease-in-out ${isSearchFocused ? 'sm:w-[340px] md:w-[390px]' : ''}`}
                    layout
                  >
                    <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${isSearchFocused ? 'text-gray-700' : 'text-gray-400'} h-4 w-4 transition-colors duration-300`} />
                    <Input 
                      placeholder="Search..." 
                      value={searchQuery}
                      onChange={(e) => onSearchChange(e.target.value)}
                      onFocus={() => setIsSearchFocused(true)}
                      onBlur={() => setIsSearchFocused(false)}
                      className={`pl-10 h-9 sm:h-10 border-gray-200 shadow-sm ${isSearchFocused ? 'ring-2 ring-gray-200 ring-opacity-50' : ''} transition-all duration-300`}
                    />
                    <AnimatePresence>
                      {searchQuery && (
                        <motion.button
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          transition={{ duration: 0.15 }}
                          onClick={() => onSearchChange('')}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-2 -mr-2"
                        >
                          <X className="h-4 w-4" />
                        </motion.button>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )}

                {/* Filter button with enhanced animation */}
                {filterButton && !isSimpleMobileLayout && (
                  <AnimatePresence>
                    <motion.div
                      className="flex-shrink-0 relative"
                      whileHover={{ 
                        scale: 1.02,
                        transition: { duration: 0.2, ease: "easeOut" }
                      }}
                      whileTap={{ 
                        scale: 0.98,
                        transition: { duration: 0.1, ease: "easeIn" }
                      }}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ 
                        type: "spring", 
                        stiffness: 300, 
                        damping: 15,
                        delay: 0.25
                      }}
                    >
                      {filterButton}
                    </motion.div>
                  </AnimatePresence>
                )}

                {!isSimpleMobileLayout && children}

                {/* Help Button - Add before the "Add" button */}
                {helpContent && !isSimpleMobileLayout && HelpButton}

                {onAddClick && !isSimpleMobileLayout && (
                  <button
                    onClick={onAddClick}
                    className={`
                      flex items-center justify-center h-9 w-9 sm:h-10 sm:w-10 rounded-lg 
                      border border-gray-200 bg-white shadow-sm transition-all duration-300 ease-in-out
                      hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-200 
                      focus:ring-offset-1 ${addButtonClassName}
                    `}
                    aria-label="Add New"
                  >
                    <Plus className={`h-5 w-5 ${addButtonClassName.includes('text-blue-700') ? 'text-blue-700' : 'text-gray-500'}`} />
                  </button>
                )}
              </motion.div>
            )}
          </div>

          {/* Description (if provided) - Only show on desktop */}
          {!isMobile && (
            <AnimatePresence>
              {description && (
                <motion.p 
                  className="text-sm text-gray-500 mb-4 sm:mb-5 max-w-3xl"
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.3 }}
                >
                  {description}
                </motion.p>
              )}
            </AnimatePresence>
          )}

          {/* Tabs Section (if provided) */}
          {tabs.length > 0 && onTabChange && (
            <motion.div 
              className={`
                ${isMobile ? 'border-b border-gray-200 mb-2 mt-2 px-0' : 'border-b border-gray-200 mb-4 sm:mb-5 mt-4 sm:mt-5 -mx-4 sm:mx-0 px-4 sm:px-0'}
              `}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.4 }}
            >
              <ScrollArea className="w-full overflow-auto pb-1">
                <div className="flex min-w-max space-x-3 sm:space-x-8">
                  {tabs.map((tab, index) => (
                    <React.Fragment key={tab.value}>
                      {(tab.separator || tab.isConfigTab) && index > 0 && (
                        <div className="h-8 w-px bg-gray-200 self-center mx-1" />
                      )}
                      <motion.button
                        onClick={() => onTabChange(tab.value)}
                        disabled={tab.disabled}
                        className={`
                          relative flex flex-row items-center py-2.5 sm:py-3 px-1 font-medium text-xs sm:text-sm transition-colors
                          ${activeTab === tab.value 
                            ? 'text-gray-900' 
                            : 'text-gray-500 hover:text-gray-700'}
                          ${tab.disabled ? 'opacity-50 cursor-not-allowed' : ''}
                        `}
                        whileHover={!tab.disabled ? { y: -1 } : {}}
                        whileTap={!tab.disabled ? { y: 0 } : {}}
                      >
                        {tab.icon && (
                          <motion.div 
                            className="mr-1.5 sm:mr-2 flex-shrink-0"
                            initial={false}
                            animate={{ rotate: activeTab === tab.value ? 0 : 0 }}
                            transition={{ type: "spring", stiffness: 300, damping: 20 }}
                          >
                            <tab.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                          </motion.div>
                        )}
                        <span>{tab.label}</span>
                        {tab.count > 0 && (
                          <motion.span 
                            className={`ml-1 sm:ml-2 py-0.5 px-1.5 sm:px-2 rounded-full text-xs ${tab.countColor || 'bg-gray-100 text-gray-700'}`}
                            initial={{ scale: 0.8 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 400, damping: 20 }}
                          >
                            {tab.count}
                          </motion.span>
                        )}
                        {activeTab === tab.value && (
                          <motion.div
                            className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900"
                            layoutId="activeTabIndicator"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ type: "spring", stiffness: 300, damping: 30 }}
                          />
                        )}
                      </motion.button>
                    </React.Fragment>
                  ))}
                </div>
              </ScrollArea>
            </motion.div>
          )}

          {/* Content Section (if provided) */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab || 'content'}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: isMobile ? 0.2 : 0.3, type: "tween" }}
            >
              {contentSection}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Help Dialog - Now with improved accessibility */}
      <Dialog open={isHelpOpen} onOpenChange={setIsHelpOpen}>
        <DialogContent className="sm:max-w-[550px] p-0 overflow-hidden border-none shadow-xl" aria-describedby="help-dialog-description">
          <DialogHeader className="px-6 py-4 border-b">
            <DialogTitle>{helpTitle}</DialogTitle>
            <DialogDescription id="help-dialog-description" className="sr-only">
              Help and documentation for using this feature
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="p-6 max-h-[70vh]">
            <div className="prose prose-sm">
              <p className="text-center text-gray-500 mb-4">
                For detailed help and documentation, use our comprehensive guide:
              </p>

              <div className="flex justify-center mt-6">
                <Button
                  className="w-full"
                  onClick={handleOpenHelp}
                >
                  Open Help Guide
                </Button>
              </div>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PageHeader;
